// ============================================================================
// apps/web/src/lib/use-candidatures-count.ts
// ----------------------------------------------------------------------------
// Compteur de candidatures « nouveau » (non encore traitées) — alimente le
// badge de l'entrée « Candidatures » (sidebar, tiroir mobile, tuile accueil).
//
// Le compteur MONTE quand une candidature arrive (dépôt public /postuler,
// statut par défaut 'nouveau') et REDESCEND quand un manager la traite (change
// son statut). Réservé aux détenteurs de `candidatures.manage` (RLS bloque de
// toute façon les autres → 0).
//
// Rafraîchi : au montage, au retour sur l'onglet (focus / visibilité), toutes
// les 60 s, et à la demande via `notifyCandidaturesChanged()` (appelé par la
// page CVthèque après un changement de statut / suppression).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';

// Table absente des types générés (db:types requiert un token) → accès non typé,
// comme la page CandidaturesPage.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase.from.bind(supabase) as unknown as (t: string) => any;

const EVENT = 'magic:candidatures-changed';

// À appeler après tout changement (dépôt, changement de statut, suppression)
// pour rafraîchir le badge immédiatement, sans attendre le prochain cycle.
export function notifyCandidaturesChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function useNewCandidaturesCount(): number {
  const { hasCapability } = useAuth();
  const canSee =
    hasCapability(Capability.CANDIDATURES_MANAGE) || hasCapability(Capability.ADMIN_ALL);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    const { count: c, error } = await db('candidatures')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'nouveau');
    if (!error) setCount(c ?? 0);
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
